import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listPublishedProducts, categoryLabel, formatPrice, CATEGORIES } from '../services/marketplaceService';
import { toast } from './toastBus';

// ═══════════════════════════════════════════════════════════
//  MARKETPLACE — buyer browse grid (published products)
// ═══════════════════════════════════════════════════════════

const CAT_ICONS = { ebook: 'menu_book', tiktok_account: 'music_note', flow_ultra: 'bolt' };

function isSoldOut(p) {
  return p.status === 'sold_out' || p.stock === 0;
}

function discountPct(p) {
  if (!p.originalPrice || p.originalPrice <= p.price) return 0;
  return Math.round((1 - p.price / p.originalPrice) * 100);
}

export default function MarketplaceView() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        setProducts(await listPublishedProducts());
      } catch (e) {
        toast.error('Gagal muat marketplace: ' + (e?.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = cat === 'all' ? products : products.filter((p) => p.category === cat);

  return (
    <div style={S.wrap}>
      <div className="sesaat-m-tight" style={S.page}>
        {/* ─── Header ─── */}
        <div style={S.headerRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={S.headerIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 24, color: '#fff' }}>storefront</span>
            </div>
            <div>
              <h1 style={S.h1}>Marketplace</h1>
              <p style={S.sub}>Akaun TikTok, Flow Ultra &amp; ebook — sedia untuk dibeli.</p>
            </div>
          </div>
          <button className="mkt-ghost" style={S.lockerBtn} onClick={() => navigate('/marketplace/pembelian')}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>receipt_long</span> Pembelian Saya
          </button>
        </div>

        {/* ─── Category filter ─── */}
        <div style={S.filters}>
          <button className="mkt-chip" style={cat === 'all' ? S.chipActive : S.chip} onClick={() => setCat('all')}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>apps</span> Semua
          </button>
          {CATEGORIES.map((c) => (
            <button key={c.id} className="mkt-chip" style={cat === c.id ? S.chipActive : S.chip} onClick={() => setCat(c.id)}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{CAT_ICONS[c.id]}</span> {c.label}
            </button>
          ))}
        </div>

        {/* ─── Grid ─── */}
        {loading ? (
          <div style={S.grid}>
            {[1, 2, 3, 4].map((i) => <div key={i} style={S.skeleton} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div style={S.empty}>
            <span className="material-symbols-outlined" style={{ fontSize: 44, color: '#d1d5db' }}>production_quantity_limits</span>
            <div>Tiada produk dalam kategori ini buat masa sekarang.</div>
          </div>
        ) : (
          <div style={S.grid}>
            {filtered.map((p) => {
              const soldOut = isSoldOut(p);
              const free = p.pricingMode === 'free';
              const disc = discountPct(p);
              return (
                <button key={p.id} className="mkt-card" style={S.card} onClick={() => navigate(`/marketplace/p/${p.id}`)}>
                  <div style={{ ...S.thumb, backgroundImage: p.imageUrl ? `url(${p.imageUrl})` : 'none' }}>
                    {!p.imageUrl && (
                      <span className="material-symbols-outlined" style={{ color: '#c7d2fe', fontSize: 40 }}>
                        {CAT_ICONS[p.category] || 'storefront'}
                      </span>
                    )}
                    <span style={S.catPill}>
                      <span className="material-symbols-outlined" style={{ fontSize: 13 }}>{CAT_ICONS[p.category]}</span>
                      {categoryLabel(p.category)}
                    </span>
                    {free && !soldOut && <span style={S.freeRibbon}>PERCUMA</span>}
                    {disc > 0 && !free && !soldOut && <span style={S.discRibbon}>-{disc}%</span>}
                    {soldOut && (
                      <div style={S.soldOutOverlay}><span style={S.soldOutText}>SOLD OUT</span></div>
                    )}
                  </div>
                  <div style={{ padding: '12px 14px 14px', textAlign: 'left' }}>
                    <div style={S.title}>{p.title}</div>
                    <div style={S.priceRow}>
                      {free ? (
                        <span style={S.free}>RM0.00</span>
                      ) : (
                        <>
                          <span style={S.price}>{formatPrice(p.price)}</span>
                          {p.originalPrice > p.price && <span style={S.orig}>{formatPrice(p.originalPrice)}</span>}
                        </>
                      )}
                    </div>
                    {typeof p.stock === 'number' && !soldOut && p.stock <= 5 && (
                      <div style={S.lowStock}>
                        <span className="material-symbols-outlined" style={{ fontSize: 13 }}>local_fire_department</span>
                        Tinggal {p.stock} sahaja
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        .mkt-card { transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
        .mkt-card:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(79,70,229,.12); border-color: #c7d2fe !important; }
        .mkt-chip { transition: all .15s ease; }
        .mkt-chip:hover { border-color: #a5b4fc !important; color: #4f46e5 !important; }
        .mkt-ghost:hover { border-color: #a5b4fc !important; color: #4f46e5 !important; }
        @keyframes mktPulse { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
      `}</style>
    </div>
  );
}

const S = {
  wrap: { height: '100%', overflowY: 'auto', background: '#f8fafc' },
  page: { padding: '28px 32px 48px', maxWidth: 1140, margin: '0 auto' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 22, flexWrap: 'wrap' },
  headerIcon: { width: 46, height: 46, borderRadius: 13, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 16px rgba(99,102,241,.3)', flexShrink: 0 },
  h1: { fontSize: 24, fontWeight: 800, margin: 0, color: '#111827', letterSpacing: '-0.02em' },
  sub: { margin: '2px 0 0', color: '#6b7280', fontSize: 13.5 },
  lockerBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  filters: { display: 'flex', gap: 8, marginBottom: 22, flexWrap: 'wrap' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 15px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#6b7280', fontFamily: 'inherit' },
  chipActive: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 15px', borderRadius: 999, border: '1px solid #4f46e5', background: '#4f46e5', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(79,70,229,.25)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(235px, 1fr))', gap: 18 },
  card: { border: '1px solid #e8eaf0', borderRadius: 16, overflow: 'hidden', background: '#fff', cursor: 'pointer', padding: 0, textAlign: 'left', fontFamily: 'inherit', boxShadow: '0 1px 3px rgba(16,24,40,.05)' },
  thumb: { aspectRatio: '4 / 3', background: '#eef2ff center/cover no-repeat', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' },
  catPill: { position: 'absolute', bottom: 8, left: 8, display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(17,24,39,.72)', color: '#fff', fontSize: 10.5, fontWeight: 700, padding: '3px 9px', borderRadius: 999, backdropFilter: 'blur(4px)' },
  freeRibbon: { position: 'absolute', top: 10, right: 10, background: '#059669', color: '#fff', fontSize: 10.5, fontWeight: 800, padding: '4px 10px', borderRadius: 8, letterSpacing: '.04em', boxShadow: '0 3px 8px rgba(5,150,105,.35)' },
  discRibbon: { position: 'absolute', top: 10, right: 10, background: '#e11d48', color: '#fff', fontSize: 11, fontWeight: 800, padding: '4px 9px', borderRadius: 8, boxShadow: '0 3px 8px rgba(225,29,72,.35)' },
  soldOutOverlay: { position: 'absolute', inset: 0, background: 'rgba(255,255,255,.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'grayscale(1)' },
  soldOutText: { background: '#111827', color: '#fff', fontSize: 12, fontWeight: 800, padding: '6px 16px', borderRadius: 999, letterSpacing: '.08em', transform: 'rotate(-6deg)' },
  title: { fontWeight: 700, fontSize: 14.5, color: '#111827', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 39 },
  priceRow: { display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 8 },
  price: { fontWeight: 800, fontSize: 17, color: '#4f46e5' },
  orig: { fontSize: 12.5, color: '#9ca3af', textDecoration: 'line-through' },
  free: { fontWeight: 800, fontSize: 17, color: '#059669' },
  lowStock: { display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8, fontSize: 11, fontWeight: 700, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', padding: '3px 9px', borderRadius: 999 },
  skeleton: { borderRadius: 16, background: '#e5e7eb', aspectRatio: '4 / 4.6', animation: 'mktPulse 1.4s ease-in-out infinite' },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, color: '#9ca3af', fontSize: 14, padding: '64px 0', textAlign: 'center' },
};
